import { useEffect, useMemo, useState } from 'react'
import {
  getClient,
  getGetAllCategoriesInLocation,
  getCityBySlug,
} from '../../../lib/sanity.client'
import { usePageType } from '../../../lib/hooks/usePageType'

type HeaderLabel = { id: string; label: string }

export type MatchedHeaderCategory = { _id: string; label: string; category: any }

/**
 * Fetch and match header categories for the current city/location and language.
 */
export function useHeaderCategories(options: {
  isCityPage: boolean
  locationId?: string | null
  language: string
  headerLabels: HeaderLabel[]
  queryCity?: string | string[] | undefined
}) {
  const { isCityPage, locationId, language, headerLabels, queryCity } = options
  const [matchedHeaderCategories, setMatchedHeaderCategories] = useState<
    MatchedHeaderCategory[]
  >([])

  const labels = useMemo(() => headerLabels, [headerLabels])
  const { type: pageType, categorySlug } = usePageType()

  useEffect(() => {
    const fetchCategories = async () => {
      const isEligiblePage = isCityPage || pageType === 'category'
      // If page is not eligible, clear and stop
      if (!isEligiblePage) {
        setMatchedHeaderCategories([])
        return
      }
      const client = getClient()
      try {
        // Resolve effective location id:
        // 1) Use provided context id when available
        // 2) Fallback: resolve by city slug from the URL if context id is missing
        let effectiveLocationId: string | undefined | null = locationId
        if (!effectiveLocationId && queryCity) {
          const citySlug = Array.isArray(queryCity) ? queryCity[0] : queryCity
          if (typeof citySlug === 'string' && citySlug.length > 0) {
            const city = await getCityBySlug(language as AppLocale, client, citySlug)
            effectiveLocationId = city?._id
          }
        }

        if (!effectiveLocationId) {
          // Keep previous categories instead of clearing to avoid flicker
          return
        }

        const fetchFn = getGetAllCategoriesInLocation(effectiveLocationId)
        const categories = await fetchFn(language as AppLocale, client, -1)
        const byPrompt = new Map(
          (categories || [])
            .filter((c: any) => !!c?.prompt_id)
            .map((c: any) => [c.prompt_id as string, c]),
        )

        let items = labels
          .map(({ id, label }) => {
            const match = byPrompt.get(id)
            if (!match) return null
            return { _id: id, label, category: match }
          })
          .filter(Boolean) as MatchedHeaderCategory[]

        // On category pages, exclude the currently viewed category
        if (pageType === 'category' && categorySlug) {
          items = items.filter((item) => {
            const localizedSlug = item?.category?.slug?.[
              language as AppLocale
            ]?.current
            return localizedSlug !== categorySlug
          })
        }

        setMatchedHeaderCategories(items)
      } catch (e) {
        // On error, keep previous categories to avoid empty header
        return
      }
    }
    fetchCategories()
  }, [isCityPage, locationId, language, queryCity, labels, pageType, categorySlug])

  return matchedHeaderCategories
}


